import { Coordinate } from 'ol/coordinate';
import {
  BusRoute,
  BusStop,
  Direction,
  Ladder,
  BusRouteDataMap,
  StopCodeNameMap,
  StreetLines,
  RouteStop,
} from '../constant/route-types';
import { Stop } from '../../types/mock-timetable-type';
import moment from 'moment';
import { getTimeDifference } from './demo-timer';

export function generateAllRouteLadders(
  map: BusRouteDataMap,
  streetLines: StreetLines
): BusRouteDataMap {
  const updatedMap: BusRouteDataMap = {};

  Object.entries(map).forEach(([routeId, routeData]) => {
    const newRoute = generateRouteLadder(routeData, streetLines);
    updatedMap[routeId] = {
      ...newRoute,
    };
  });

  return updatedMap;
}

export const generateRouteLadder = (
  route: BusRoute,
  streetLines: StreetLines
): BusRoute => {
  const { busIdStr, stops } = route;

  const newDirection: Direction[] = stops.map(dir => {
    const { stop, direction } = dir;
    const newStops: RouteStop[] = stop.map(stopItem => {
      const { stopCode, stopSequence } = stopItem;
      const stopData = findBusStopByCode(streetLines, stopCode);
      const { stopName, streetName, stopLat, stopLon } = stopData;
      const newStop: RouteStop = {
        stopCode,
        stopSequence,
        latitude: +stopLat,
        longitude: +stopLon,
        stopName,
        streetName,
        mrtStationCode: '',
        mrtStationName: '',
        ctrCode: null,
      };
      return newStop;
    });
    return {
      value: `${direction}`,
      stops: newStops,
    };
  });

  const newLadder: Ladder = {
    isVisible: 'true',
    routeId: [busIdStr],
    routeName: [busIdStr],
    direction: newDirection,
  };

  return {
    ...route,
    ladder: newLadder,
  };
};

/**
 * Finds a BusStop in demoStreetLines by stopCode.
 * @param lines The demoStreetLines array
 * @param stopCode The stopCode to search for
 * @returns The matching BusStop, or undefined if not found
 */
function findBusStopByCode(
  lines: StreetLines,
  stopCode: string
): BusStop | undefined {
  return lines
    .flatMap(line => line.stops)
    .find(stop => stop.stopCode === stopCode);
}

export function createStopCodeNameMap(lines: StreetLines): StopCodeNameMap {
  return Object.fromEntries(
    lines.flatMap(line =>
      line.stops.map(stop => [stop.stopCode, stop.stopName])
    )
  );
}

/**
 * Returns the index of the coordinate in the array closest to the target point.
 */
export function getClosestVertexIndex(
  target: Coordinate,
  coords: Coordinate[]
): number {
  let minDist = Infinity;
  let closestIndex = -1;

  coords.forEach((coord, i) => {
    const dx = coord[0] - target[0];
    const dy = coord[1] - target[1];
    const dist = dx * dx + dy * dy;

    if (dist < minDist) {
      minDist = dist;
      closestIndex = i;
    }
  });

  return closestIndex;
}

export function findEtaFromStopSequence(
  stopSequence: number,
  stops: Stop[]
): number {
  const currentStop = stops.find(stop => stop.stopSequence === stopSequence);
  if (currentStop) {
    const currentTime = moment().format('HH:mm:ss');
    const timeDiff = getTimeDifference(currentTime, currentStop.schArrTime);
    return timeDiff;
  }
  return undefined;
}

export function findEtaForFirstSequence(stops: Stop[]): number {
  const prevStop = stops.find(stop => stop.stopSequence === 1);
  const currentStop = stops.find(stop => stop.stopSequence === 2);
  if (currentStop) {
    const timeDiff = getTimeDifference(
      prevStop.schArrTime,
      currentStop.schArrTime
    );
    return timeDiff;
  }
  return undefined;
}

export function getNextValidStops(
  stops: Stop[],
  startIndex: number,
  count = 3
): Stop[] {
  const effectiveStartIndex = startIndex < 0 ? -1 : startIndex;
  const validStops = stops.reduce((acc, stop, index) => {
    if (index <= effectiveStartIndex || acc.length >= count) return acc;
    if (!stop.skip) acc.push(stop);
    return acc;
  }, []);
  return Array.from({ length: count }, (_, i) => validStops[i]);
}

export function getNextValidStopsById(
  stops: Stop[],
  stopCode: string,
  count = 3
): Stop[] {
  const startIndex = stops.findIndex(stop => stop.stopId === stopCode);
  if (startIndex === -1) {
    return Array(count).fill(undefined);
  }
  const validStops = stops.reduce((acc, stop, index) => {
    if (index <= startIndex || acc.length >= count) return acc;
    if (!stop.skip) acc.push(stop);
    return acc;
  }, []);
  return Array.from({ length: count }, (_, i) => validStops[i]);
}
